// src/helper/jobs/jobLocalCacheHelper.js
//
// Client-side persistence for Jobs for You + Tailor Resume.
//
// WHY THIS EXISTS
//   Router state dies on refresh. The tailor page currently stores the job in
//   location.state only, so a refreshed /#/jobs/tailor tab loses the posting and
//   every AI response. Search results are similarly wiped even though the
//   server already cached the same query for 6h — forcing another round trip
//   (and another auto-score) for no reason.
//
// WHAT IS STORED
//   - One "last search" blob per signed-in uid (filters + jobs + scores)
//   - One tailor blob per jobId (job snapshot + ATS/summary/bullets edits)
//   - A pointer to the last tailor jobId so a refresh with no router state
//     still lands on the posting the user was working on
//
// All storage access is guarded the same way jobHandoffHelper does it —
// Safari private mode must never take the page down.

import get from "lodash/get";
import isEmpty from "lodash/isEmpty";

import {
  DEFAULT_JOB_SEARCH_FILTERS,
  DEFAULT_JOB_SITES,
  JOB_RESULTS_WANTED,
  MAX_JOB_LOCATION_CHARS,
  MAX_JOB_SITES,
  MAX_JOB_TITLE_CHARS,
} from "../../utils/jobs/jobConstants";

// Duplicated from jobHandoffHelper on purpose — importing that module here
// would create a circular dependency (handoff -> cache -> handoff).
const safeSetLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    return false;
  }
};

const safeGetLocalStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const safeRemoveLocalStorage = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    return false;
  }
};

export const JOBS_SEARCH_CACHE_KEY = "jobsForYouSearchCache";
export const JOBS_TAILOR_LAST_JOB_KEY = "jobsForYouTailorLastJobId";
export const JOBS_TAILOR_CACHE_PREFIX = "jobsForYouTailor:";

// Mirror the server job-search-cache TTL so a restored result set is never
// older than what the API would have returned as "cached".
export const JOBS_SEARCH_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

// Tailor output is user work product (including edits). Keep it longer.
export const JOBS_TAILOR_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const CACHE_VERSION = 1;

const clampText = (value, maxChars) =>
  typeof value === "string" ? value.trim().slice(0, maxChars) : "";

const clampNumber = (value, min, max, fallback) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) return fallback;

  return Math.min(max, Math.max(min, Math.round(parsed)));
};

const normalizeSites = (sites) => {
  const list =
    Array.isArray(sites) && sites.length ? sites : DEFAULT_JOB_SITES;

  return [
    ...new Set(
      list.map((site) => String(site || "").trim()).filter(Boolean)
    ),
  ]
    .slice(0, MAX_JOB_SITES)
    .sort();
};

/**
 * Canonical query fingerprint for client cache identity. Intentionally mirrors
 * useJobSearch's toRequestBody fields (minus forceRefresh) so "same filters"
 * means the same thing on both sides of the round trip.
 */
export const buildSearchQueryKey = (filters) => {
  const query = {
    title: clampText(
      get(filters, "title", ""),
      MAX_JOB_TITLE_CHARS
    ).toLowerCase(),
    location: clampText(
      get(filters, "location", ""),
      MAX_JOB_LOCATION_CHARS
    ).toLowerCase(),
    workMode: get(filters, "workMode", DEFAULT_JOB_SEARCH_FILTERS.workMode),
    datePostedHours: clampNumber(
      get(filters, "datePostedHours", null),
      0,
      720,
      DEFAULT_JOB_SEARCH_FILTERS.datePostedHours
    ),
    jobType: get(filters, "jobType", DEFAULT_JOB_SEARCH_FILTERS.jobType),
    sites: normalizeSites(get(filters, "sites", DEFAULT_JOB_SITES)),
    countryIndeed: get(
      filters,
      "countryIndeed",
      DEFAULT_JOB_SEARCH_FILTERS.countryIndeed
    ),
    resultsWanted: clampNumber(
      get(filters, "resultsWanted", null),
      JOB_RESULTS_WANTED.MIN,
      JOB_RESULTS_WANTED.MAX,
      JOB_RESULTS_WANTED.DEFAULT
    ),
  };

  return JSON.stringify(query);
};

export const queriesMatch = (left, right) => {
  if (!left || !right) return false;

  return buildSearchQueryKey(left) === buildSearchQueryKey(right);
};

const readJson = (key) => {
  const raw = safeGetLocalStorage(key);

  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch (error) {
    safeRemoveLocalStorage(key);
    return null;
  }
};

const writeJson = (key, value) =>
  safeSetLocalStorage(key, JSON.stringify(value));

const isFresh = (savedAt, ttlMs) => {
  const stamp = Number(savedAt);

  if (!Number.isFinite(stamp) || stamp <= 0) return false;

  return Date.now() - stamp < ttlMs;
};

// ---------------------------------------------------------------------------
// Search cache (one blob per uid — last successful / restored search)
// ---------------------------------------------------------------------------

export const loadJobsSearchCache = (uid) => {
  if (!uid) return null;

  const cached = readJson(JOBS_SEARCH_CACHE_KEY);

  if (!cached || cached.version !== CACHE_VERSION) return null;
  if (cached.uid !== uid) return null;
  if (!isFresh(cached.savedAt, JOBS_SEARCH_CACHE_TTL_MS)) {
    safeRemoveLocalStorage(JOBS_SEARCH_CACHE_KEY);
    return null;
  }

  return cached;
};

export const saveJobsSearchCache = (uid, payload) => {
  if (!uid || isEmpty(payload)) return false;

  const filters = get(payload, "filters", DEFAULT_JOB_SEARCH_FILTERS);
  const next = {
    version: CACHE_VERSION,
    uid,
    savedAt: Date.now(),
    filters,
    queryKey: buildSearchQueryKey(filters),
    lastSearchedQuery: get(payload, "lastSearchedQuery", null),
    results: Array.isArray(get(payload, "results", null))
      ? get(payload, "results", [])
      : [],
    status: get(payload, "status", ""),
    providerName: get(payload, "providerName", ""),
    perSite: get(payload, "perSite", {}) || {},
    fallbackLinks: Array.isArray(get(payload, "fallbackLinks", null))
      ? get(payload, "fallbackLinks", [])
      : [],
    degradedReason: get(payload, "degradedReason", null),
    currentPage: Number(get(payload, "currentPage", 0)) || 0,
    scores: get(payload, "scores", {}) || {},
    resumeHash: get(payload, "resumeHash", "") || "",
    restoredHint: true,
  };

  return writeJson(JOBS_SEARCH_CACHE_KEY, next);
};

export const patchJobsSearchCache = (uid, patch) => {
  const existing = loadJobsSearchCache(uid);

  if (!existing) return false;

  return saveJobsSearchCache(uid, { ...existing, ...(patch || {}) });
};

export const clearJobsSearchCache = () =>
  safeRemoveLocalStorage(JOBS_SEARCH_CACHE_KEY);

// ---------------------------------------------------------------------------
// Tailor cache (one blob per jobId)
// ---------------------------------------------------------------------------

const tailorKeyFor = (jobId) => `${JOBS_TAILOR_CACHE_PREFIX}${jobId}`;

export const getTailorJobId = (job) => {
  const jobId = String(get(job, "jobId", "") || "").trim();

  if (jobId) return jobId;

  const title = String(get(job, "title", "") || "")
    .trim()
    .toLowerCase();
  const company = String(get(job, "company", "") || "")
    .trim()
    .toLowerCase();

  if (!title) return "";

  return `title:${title}|${company}`;
};

export const loadJobsTailorCache = (jobId) => {
  const id = String(jobId || "").trim();

  if (!id) return null;

  const cached = readJson(tailorKeyFor(id));

  if (!cached || cached.version !== CACHE_VERSION) return null;
  if (!isFresh(cached.savedAt, JOBS_TAILOR_CACHE_TTL_MS)) {
    safeRemoveLocalStorage(tailorKeyFor(id));
    return null;
  }

  return cached;
};

export const saveJobsTailorCache = (jobId, payload) => {
  const id = String(jobId || "").trim();

  if (!id || isEmpty(payload)) return false;

  const next = {
    version: CACHE_VERSION,
    jobId: id,
    savedAt: Date.now(),
    job: get(payload, "job", null),
    atsFeedback: get(payload, "atsFeedback", "") || "",
    tailoredSummary: get(payload, "tailoredSummary", "") || "",
    tailoredExperience: get(payload, "tailoredExperience", "") || "",
    hasRequestedFeedback: Boolean(get(payload, "hasRequestedFeedback", false)),
  };

  const written = writeJson(tailorKeyFor(id), next);

  if (written) {
    safeSetLocalStorage(JOBS_TAILOR_LAST_JOB_KEY, id);
  }

  return written;
};

export const patchJobsTailorCache = (jobId, patch) => {
  const existing = loadJobsTailorCache(jobId) || {
    job: null,
    atsFeedback: "",
    tailoredSummary: "",
    tailoredExperience: "",
    hasRequestedFeedback: false,
  };

  return saveJobsTailorCache(jobId, { ...existing, ...(patch || {}) });
};

export const loadLastJobsTailorCache = () => {
  const lastId = safeGetLocalStorage(JOBS_TAILOR_LAST_JOB_KEY);

  if (!lastId) return null;

  return loadJobsTailorCache(lastId);
};

export const rememberTailorJob = (job) => {
  const jobId = getTailorJobId(job);

  if (!jobId || isEmpty(job)) return "";

  const existing = loadJobsTailorCache(jobId) || {};

  saveJobsTailorCache(jobId, {
    ...existing,
    job,
    atsFeedback: get(existing, "atsFeedback", ""),
    tailoredSummary: get(existing, "tailoredSummary", ""),
    tailoredExperience: get(existing, "tailoredExperience", ""),
    hasRequestedFeedback: Boolean(get(existing, "hasRequestedFeedback", false)),
  });

  return jobId;
};

export default {
  JOBS_SEARCH_CACHE_KEY,
  JOBS_TAILOR_LAST_JOB_KEY,
  JOBS_TAILOR_CACHE_PREFIX,
  JOBS_SEARCH_CACHE_TTL_MS,
  JOBS_TAILOR_CACHE_TTL_MS,
  buildSearchQueryKey,
  queriesMatch,
  loadJobsSearchCache,
  saveJobsSearchCache,
  patchJobsSearchCache,
  clearJobsSearchCache,
  getTailorJobId,
  loadJobsTailorCache,
  saveJobsTailorCache,
  patchJobsTailorCache,
  loadLastJobsTailorCache,
  rememberTailorJob,
};
